import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  LoginPage,
  AdminDashboard,
  UsersPage,
  CompaniesPage,
  CompanyManagementPage,
  BlogAdmin,
  AnalyticsPage,
  APIMonitoringPage,
  CustomerSupportPage,
  InfrastructurePage,
  AdminBillingPage
} from './pages';
import AdminUserManagement from './components/AdminUserManagement';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoadingScreen from './components/LoadingScreen';

const theme = createTheme({
  palette: {
    primary: {
      main: '#6B46C1', // Purple color from the design
    },
    secondary: {
      main: '#9F7AEA',
    },
    background: {
      default: '#F7FAFC',
      paper: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
});

function AppContent() {
  const { isLoading, hasStoredToken } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  // If no stored token, show login page immediately
  if (!hasStoredToken()) {
    return (
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/home" element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/users" element={
          <ProtectedRoute>
            <UsersPage />
          </ProtectedRoute>
        } />
        <Route path="/companies" element={
          <ProtectedRoute>
            <CompaniesPage />
          </ProtectedRoute>
        } />
        <Route path="/companies/:id" element={
          <ProtectedRoute>
            <CompanyManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/analytics" element={
          <ProtectedRoute>
            <AnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="/blog" element={
          <ProtectedRoute>
            <BlogAdmin />
          </ProtectedRoute>
        } />
        <Route path="/api-monitoring" element={
          <ProtectedRoute>
            <APIMonitoringPage />
          </ProtectedRoute>
        } />
        <Route path="/api-monitoring/health" element={
          <ProtectedRoute>
            <APIMonitoringPage />
          </ProtectedRoute>
        } />
        <Route path="/api-monitoring/metrics" element={
          <ProtectedRoute>
            <APIMonitoringPage />
          </ProtectedRoute>
        } />
        <Route path="/api-monitoring/response-times" element={
          <ProtectedRoute>
            <APIMonitoringPage />
          </ProtectedRoute>
        } />
        <Route path="/api-monitoring/errors" element={
          <ProtectedRoute>
            <APIMonitoringPage />
          </ProtectedRoute>
        } />
        <Route path="/support" element={
          <ProtectedRoute>
            <CustomerSupportPage />
          </ProtectedRoute>
        } />
        <Route path="/infrastructure" element={
          <ProtectedRoute>
            <InfrastructurePage />
          </ProtectedRoute>
        } />
        <Route path="/billing" element={
          <ProtectedRoute>
            <AdminBillingPage />
          </ProtectedRoute>
        } />
        <Route path="/billing/promos" element={
          <ProtectedRoute>
            <AdminBillingPage />
          </ProtectedRoute>
        } />
        <Route path="/admin-users" element={
          <ProtectedRoute>
            <AdminUserManagement />
          </ProtectedRoute>
        } />
        <Route path="/roles" element={
          <ProtectedRoute>
            <AdminUserManagement />
          </ProtectedRoute>
        } />
        <Route path="/impersonate" element={
          <ProtectedRoute>
            <AdminUserManagement />
          </ProtectedRoute>
        } />
        <Route path="/" element={<Navigate to="/home" replace />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App; 